import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Field,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { InputField, TextareaField } from '@themes/aegix';
import { Switch } from '@chakra-ui/react';
import {
  ColorPickerInput,
  DataTable,
  Pagination,
  StatusBadge,
  TableOptions,
  Button as ButtonComponent,
  type StatusType,
} from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useForm, Controller, Resolver } from 'react-hook-form';
import type {
  ConfigurableTypeFormValues,
  TypeCategory,
} from '../../types/ReunificationTypes';
import { useConfigurableTypeCrud } from './hooks/useConfigurableTypeCrud';
import { zodResolver } from '@hookform/resolvers/zod';
import { configurableTypeFormSchema } from '../../validations/reunification.validation';

interface ConfigurableTypeManagerProps {
  category: TypeCategory;
}

export function ConfigurableTypeManager({
  category,
}: ConfigurableTypeManagerProps) {
  const { t } = useTranslation();
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    sortColumn,
    sortDirection,
    drawerOpen,
    actionMode,
    filterValues,
    systemFilter,
    setSystemFilter,
    isLoading,
    error,
    pagedTypes,
    totalItems,
    totalPages,
    columns,
    isSystemType,
    label,
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleDrawerClose,
    openAddDrawer,
    openEditDrawer,
    handleSubmit,
    handleDelete,
    getInitialValues,
  } = useConfigurableTypeCrud(category);

  const methods = useForm<ConfigurableTypeFormValues>({
    defaultValues: getInitialValues(),
    resolver: zodResolver(configurableTypeFormSchema) as unknown as Resolver<ConfigurableTypeFormValues>,
    mode: 'onBlur',
  });

  const {
    control,
    handleSubmit: rhfHandleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  // enableReinitialize equivalent
  useEffect(() => {
    reset(getInitialValues());
  }, [actionMode, drawerOpen, getInitialValues, reset]);

  const formValues = watch();

  const onSubmit = (values: ConfigurableTypeFormValues) => {
    handleSubmit(values, methods.setError);
  };

  // Add render functions to columns from the hook
  const renderColumns = columns.map((col) => {
    if (col.key === 'description') {
      return {
        ...col,
        render: (row: { description: string }) => (
          <Text fontSize="sm" lineClamp={1}>
            {row.description || '\u2014'}
          </Text>
        ),
      };
    }
    if (col.key === 'system_status') {
      return {
        ...col,
        render: (row: { is_system: boolean }) =>
          row.is_system ? (
            <StatusBadge status={'info' as StatusType} label={t('admin.system', 'System')} />
          ) : null,
      };
    }
    if (col.key === 'active_status') {
      return {
        ...col,
        render: (row: { is_active: boolean }) => (
          <StatusBadge
            status={
              row.is_active
                ? ('active' as StatusType)
                : ('inactive' as StatusType)
            }
            label={row.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
          />
        ),
      };
    }
    return col;
  });

  return (
    <Box>
      <TableOptions
        toggleOptions={[
          { value: 'all', label: t('common.all', 'All') },
          { value: 'system', label: t('admin.system', 'System') },
          { value: 'custom', label: t('admin.custom', 'Custom') },
        ]}
        activeToggle={systemFilter}
        onToggleChange={setSystemFilter}
        primaryAction={{ label: `${t('common.add', 'Add')} ${label}`, onClick: openAddDrawer }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : t('admin.failedLoadTypes', 'Failed to load types')}
            </Text>
          </Center>
        ) : pagedTypes.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{t('admin.noTypesFound', 'No types found')}</Text>
          </Center>
        ) : (
          <>
            <DataTable
              columns={renderColumns}
              data={pagedTypes}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={openEditDrawer}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </>
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={handleDrawerClose}
        title={
          actionMode === 'add'
            ? t('admin.addType', 'Add Type')
            : isSystemType
              ? t('admin.viewSystemType', 'View System Type')
              : t('admin.editType', 'Edit Type')
        }
        size="md"
        closeOnOutside={false}
        closeOnEsc={false}
        footer={
          <SimpleGrid
            columns={
              actionMode === 'edit' && !isSystemType ? 3 : isSystemType ? 1 : 2
            }
            gap={4}
            width="100%"
          >
            {actionMode === 'edit' && !isSystemType && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
                data-e2e="delete-type-button"
              >
                {t('common.delete', 'Delete')}
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={handleDrawerClose}
              data-e2e="cancel-type-button"
            >
              {isSystemType ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
            </ButtonComponent>
            {!isSystemType && (
              <ButtonComponent
                visual="primary"
                type="submit"
                form="configurable-type-form"
                data-e2e="save-type-button"
              >
                {t('common.save', 'Save')}
              </ButtonComponent>
            )}
          </SimpleGrid>
        }
      >
        <form id="configurable-type-form" onSubmit={rhfHandleSubmit(onSubmit)}>
          <VStack gap={4} align="stretch">
            <Field.Root invalid={!!errors.name}>
              <Field.Label htmlFor="type-name">{t('common.name', 'Name')}</Field.Label>
              <InputField
                id="type-name"
                name="name"
                placeholder={t('admin.enterTypeName', 'Enter type name')}
                value={formValues.name}
                onChange={(e) => {
                  setValue('name', e.target.value);
                  if (errors.name) methods.clearErrors('name');
                }}
                disabled={!!isSystemType}
                data-e2e="configurable-type-name-input"
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'type-name-error' : undefined}
              />
              {errors.name && (
                <Field.ErrorText id="type-name-error" data-e2e="configurable-type-name-error">
                  {errors.name.message ? t(errors.name.message) : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.code}>
              <Field.Label htmlFor="type-code">{t('admin.code', 'Code')}</Field.Label>
              <InputField
                id="type-code"
                name="code"
                placeholder={t('admin.enterTypeCode', 'Enter type code')}
                value={formValues.code}
                onChange={(e) => {
                  setValue('code', e.target.value);
                  if (errors.code) methods.clearErrors('code');
                }}
                disabled={!!isSystemType}
                data-e2e="configurable-type-code-input"
                aria-invalid={!!errors.code}
                aria-describedby={errors.code ? 'type-code-error' : undefined}
              />
              {errors.code && (
                <Field.ErrorText id="type-code-error" data-e2e="configurable-type-code-error">
                  {errors.code.message ? t(errors.code.message) : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.description}>
              <Field.Label htmlFor="type-description">{t('common.description', 'Description')}</Field.Label>
              <TextareaField
                id="type-description"
                name="description"
                placeholder={t('admin.describeThisType', 'Describe this type')}
                value={formValues.description}
                onChange={(e) => {
                  setValue('description', e.target.value);
                  if (errors.description) methods.clearErrors('description');
                }}
                rows={3}
                disabled={!!isSystemType}
                data-e2e="configurable-type-description-input"
                aria-invalid={!!errors.description}
                aria-describedby={errors.description ? 'type-description-error' : undefined}
              />
              {errors.description && (
                <Field.ErrorText id="type-description-error" data-e2e="configurable-type-description-error">
                  {errors.description.message ? t(errors.description.message) : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.icon}>
              <Field.Label htmlFor="type-icon">{t('protocols.icon', 'Icon')}</Field.Label>
              <InputField
                id="type-icon"
                name="icon"
                placeholder={t('admin.iconPlaceholder', 'Icon name or identifier')}
                value={formValues.icon}
                onChange={(e) => {
                  setValue('icon', e.target.value);
                  if (errors.icon) methods.clearErrors('icon');
                }}
                disabled={!!isSystemType}
                data-e2e="configurable-type-icon-input"
                aria-invalid={!!errors.icon}
                aria-describedby={errors.icon ? 'type-icon-error' : undefined}
              />
              {errors.icon && (
                <Field.ErrorText id="type-icon-error" data-e2e="configurable-type-icon-error">
                  {errors.icon.message ? t(errors.icon.message) : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.color}>
              <Field.Label htmlFor="type-color">{t('admin.color', 'Color')}</Field.Label>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <ColorPickerInput
                    id="type-color"
                    name="color"
                    value={field.value}
                    onChange={(val) => {
                      field.onChange(val);
                      if (errors.color) methods.clearErrors('color');
                    }}
                    disabled={!!isSystemType}
                    aria-invalid={!!errors.color}
                    aria-describedby={errors.color ? 'type-color-error' : undefined}
                  />
                )}
              />
              {errors.color && (
                <Field.ErrorText id="type-color-error" data-e2e="configurable-type-color-error">
                  {errors.color.message ? t(errors.color.message) : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root invalid={!!errors.sort_order}>
              <Field.Label htmlFor="type-sort-order">{t('admin.sortOrder', 'Sort Order')}</Field.Label>
              <Controller
                name="sort_order"
                control={control}
                render={({ field }) => (
                  <InputField
                    id="type-sort-order"
                    type="number"
                    min={0}
                    placeholder="0"
                    value={String(field.value)}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : Number(e.target.value);
                      field.onChange(val);
                      if (errors.sort_order) methods.clearErrors('sort_order');
                    }}
                    disabled={!!isSystemType}
                    data-e2e="sort-order-input"
                    aria-invalid={!!errors.sort_order}
                    aria-describedby={errors.sort_order ? 'type-sort-order-error' : undefined}
                  />
                )}
              />
              {errors.sort_order && (
                <Field.ErrorText id="type-sort-order-error" data-e2e="configurable-type-sort-order-error">
                  {errors.sort_order.message ? t(errors.sort_order.message) : null}
                </Field.ErrorText>
              )}
            </Field.Root>

            <Field.Root>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <Switch.Root
                    checked={field.value}
                    onCheckedChange={(details) =>
                      field.onChange(details.checked)
                    }
                    disabled={!!isSystemType}
                  >
                    <Switch.HiddenInput data-e2e="type-active-switch" />
                    <Switch.Control>
                      <Switch.Thumb />
                    </Switch.Control>
                    <Switch.Label>{t('common.active', 'Active')}</Switch.Label>
                  </Switch.Root>
                )}
              />
            </Field.Root>
          </VStack>
        </form>
      </DrawerComponent>
    </Box>
  );
}
